import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHolidays } from '../../../redux/slices/absenceSlice';
import { fetchTimeBalances } from '../../../redux/slices/timeBalanceSlice';
import { useTranslation } from 'react-i18next';
import './MiniCalendar.css';

const MiniCalendar = ({ selectedDate, onDateSelect }) => {
  const { t } = useTranslation();
  const [currentMonthView, setCurrentMonthView] = useState(selectedDate || new Date());
  
  const today = new Date();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const { items: absences = [] } = useSelector((state) => state.absences || {});
  const { items: holidays = [] } = useSelector((state) => state.absences || {}); 
  const { items: timeBalances = [] } = useSelector((state) => state.timeBalances || {});

  const year = currentMonthView.getFullYear();
  const month = currentMonthView.getMonth();

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchHolidays(year));
      dispatch(fetchTimeBalances({ userId: user.id, year, month: month + 1 }));
    }
  }, [dispatch, user?.id, year, month]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let firstDayIndex = new Date(year, month, 1).getDay();
  firstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1;

  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptySlots = Array.from({ length: firstDayIndex }, (_, i) => i);

  const weekDaysIndexes = [0, 1, 2, 3, 4, 5, 6]; 

  const handlePrevMonth = () => setCurrentMonthView(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentMonthView(new Date(year, month + 1, 1));
  const handleDayClick = (day) => onDateSelect?.(new Date(year, month, day));

  const getEventForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const currentLoopDate = new Date(year, month, day).setHours(0, 0, 0, 0);

    const holiday = holidays.find((h) => h.date?.split('T')[0] === dateStr);
    if (holiday) return { label: holiday.name || t('components.MiniCalendar.holiday'), color: 'var(--brand-primary)' };

    const absence = absences.find((abs) => {
      if (abs.status !== 'Accepted' || abs.user_id !== user?.id) return false;
      const start = new Date(abs.start_date).setHours(0, 0, 0, 0);
      const end = new Date(abs.end_date).setHours(0, 0, 0, 0);
      return currentLoopDate >= start && currentLoopDate <= end;
    });
    if (absence) return { label: absence.absence_type, color: '#2163ce' };

    const tb = timeBalances.find((t) => t.date === dateStr);
    if (tb && (tb.worked_minutes > 0 || tb.projects?.length > 0)) {
        return { label: `${t('components.MiniCalendar.worked')} ${Math.floor(tb.worked_minutes/60)}h`, color: 'var(--success-green)' };
    }
    
    return null;
  };

  return (
    <div className="mini-calendar-wrapper">
      <div className="atlar-card calendar-card">
        <div className="calendar-header">
          <button className="calendar-nav-btn" onClick={handlePrevMonth}>‹</button>
          <span className="calendar-title">{t(`components.MiniCalendar.months.${month}`)} {year}</span>
          <button className="calendar-nav-btn" onClick={handleNextMonth}>›</button>
        </div>

        <div className="calendar-week-grid">
          {weekDaysIndexes.map((dayIndex) => (
            <div key={dayIndex} className="week-day-label">
              {t(`components.MiniCalendar.weekDays.${dayIndex}`)}
            </div>
          ))}
        </div>

        <div className="calendar-days-grid">
          {emptySlots.map((slot) => <div key={`empty-${slot}`} />)}
          {daysArray.map((day) => {
            const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === month && selectedDate.getFullYear() === year;
            const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
            const dayEvent = getEventForDay(day);
            
            const dayContent = (
              <div className={`day-cell ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`} onClick={() => handleDayClick(day)}>
                {day}
                {dayEvent && !isSelected && <div className="event-dot" style={{ backgroundColor: dayEvent.color }} />}
              </div>
            );

            return dayEvent ? (
              <OverlayTrigger key={day} placement="top" overlay={<Tooltip style={{ fontSize: '12px' }}>{dayEvent.label}</Tooltip>}>
                <div>{dayContent}</div>
              </OverlayTrigger>
            ) : <div key={day}>{dayContent}</div>;
          })}
        </div>
      </div>
    </div>
  );
};

export default MiniCalendar;